import { useEffect, useRef } from 'react';

// 打包當下的版本（來源：package.json 的 version，由 craco.config.js 注入）
const CURRENT_VERSION = process.env.REACT_APP_VERSION ?? '';

// 記錄「已為某版本嘗試過 reload」，避免外層殼(index.html)仍被緩存時無限重整
const RELOAD_GUARD_KEY = 'vc:reloaded-for';

const readGuard = (): string | null => {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY);
  } catch {
    return null;
  }
};

const writeGuard = (v: string) => {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, v);
  } catch {
    /* 忽略 */
  }
};

const clearGuard = () => {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    /* 忽略 */
  }
};

/**
 * 前端版本檢查：載入時 & 從背景切回前景時，抓 /version.json 比對版本，
 * 發現有新版就 reload，讓 LINE WebView 這類會復用舊 session 的環境也能更新。
 */
const useVersionCheck = (): void => {
  const checkingRef = useRef(false);

  useEffect(() => {
    const check = async () => {
      if (checkingRef.current || !CURRENT_VERSION) return;
      checkingRef.current = true;

      try {
        const url = `${process.env.PUBLIC_URL ?? ''}/version.json?_=${Date.now()}`;
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) return;

        const { version: latest } = await res.json();
        if (!latest) return;

        if (latest === CURRENT_VERSION) {
          clearGuard();
          return;
        }

        // 版本不同 → 有新版。若剛剛已為這個版本 reload 過卻還是舊的，
        // 代表殼被緩存，先停手避免無限重整（等 no-cache / 快取自然過期）。
        if (readGuard() === latest) return;

        writeGuard(latest);
        window.location.reload();
      } catch {
        // 靜默失敗，不影響使用
      } finally {
        checkingRef.current = false;
      }
    };

    check();

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
};

export default useVersionCheck;
